/**
 * Correcting a company that already exists.
 *
 * Until this there was no way to change one at all: a typo in the GSTIN or the
 * bank account was permanent. PATCH /admin/companies has always existed and
 * had no caller — the capability was there, the form was not.
 *
 * No locked fields, and that is safe now rather than by assumption. The
 * invoice document used to read the live company row on every generation, so
 * editing a GSTIN restated every invoice ever issued; snapshot_seller freezes
 * the seller at raise time, so a raised invoice regenerates identically
 * whatever happens here afterwards.
 *
 * The one exception is stated on the form in a single line rather than a
 * warning block: invoices raised before that snapshot existed have null
 * columns and still fall back to the live company. They were deliberately not
 * backfilled — stamping today's details onto a document sent months ago would
 * assert something untrue — so it is a fact worth knowing, not a hazard worth
 * shouting about.
 */
import { useState } from 'react';
import { Loader2, Save, X } from 'lucide-react';
import { CompanyLogoField } from './CompanyLogo';
import CompanyFields, {
  companyPayload,
  companyToFields,
  type CompanyFieldValues,
} from './CompanyFields';
import type { Company } from '../types';

export default function CompanyEditDialog({
  company,
  saving,
  onCancel,
  onSave,
}: {
  company: Company;
  saving: boolean;
  onCancel: () => void;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const [values, setValues] = useState<CompanyFieldValues>(() =>
    companyToFields(company),
  );

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4">
      <form
        className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl"
        onSubmit={(e) => {
          e.preventDefault();
          // omitBlankAccount: an untouched bank box must leave the stored
          // number alone. Sending null would clear it, and PATCH uses
          // exclude_unset so omitting the key is what "no change" looks like.
          onSave(companyPayload(values, { omitBlankAccount: true }));
        }}
      >
        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-3">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-gray-900">
              Edit {company.name}
            </h3>
            {/* One line, stated once. Everything raised from here on is
                frozen at raise time; the back catalogue is not. */}
            <p className="mt-0.5 text-xs text-gray-500">
              Invoices raised before the seller snapshot still read these
              details live, so changes show on their documents.
            </p>
          </div>
          <button type="button" className="btn-ghost px-2 py-1" onClick={onCancel}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {/* Above the fields rather than buried at the bottom: it is the one
              control here that acts immediately rather than on Save, and a
              control that has already taken effect should not sit under a
              Save button implying otherwise. */}
          <section>
            <p className="label">Logo</p>
            <CompanyLogoField companyId={company.id} />
          </section>

          <CompanyFields
            values={values}
            onChange={setValues}
            accountHint={
              company.bankAccountNumberMasked
                ? `Currently ${company.bankAccountNumberMasked}. Leave blank to keep it.`
                : 'Leave blank to keep whatever is stored.'
            }
          />
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button type="button" className="btn-ghost text-sm" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-primary text-sm" disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save
          </button>
        </div>
      </form>
    </div>
  );
}
